import React, { ChangeEvent, useCallback, useState } from 'react';
import { debounce } from "lodash"
import AddressNotFound from './AddressNotFound';
import AddressItem from './AddressItem';
import {Address, searchAddress} from "../../../../api/address.api";
import {AddressSearchProps, AddressSuggestion} from "../../../../@types/address";

const AddressSearch: React.FC<AddressSearchProps> = ({ name, value, onAddressSelected }) => {

  const [address, setAddress] = useState<string>('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[] | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const [allSuggestions, setAllSuggestions] = useState<AddressSuggestion[] | null>(null);
  const [displayedCount, setDisplayedCount] = useState<number>(0);
  const [itemsPerPage] = useState<number>(10);
  const [useFrontendPagination, setUseFrontendPagination] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);

  const onSelectAddress = (selectedAddress: AddressSuggestion)=> {
    onAddressSelected(selectedAddress);
    setSuggestions(null);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    setAddress(value);

    if (value.length === 0) {
      setSuggestions(null);
      setAllSuggestions(null);
      setCurrentQuery('');
      setCurrentPage(1);
      setDisplayedCount(0);
      setTotal(0);
      setHasMore(false);
      setUseFrontendPagination(false);
    } else if (value.length > 2) {
      setCurrentQuery(value);
      setCurrentPage(1);
      setSuggestions(null);
      setAllSuggestions(null);
      setDisplayedCount(0);
      setTotal(0);
      setUseFrontendPagination(false);
      debouncedFetchAddressSuggestions(value, 1);
    }
  };

  const fetchAddressSuggestions = async (query: string, page: number = 1) => {
    setIsSearching(true);
    try {
      const response = await searchAddress(query, page);
      if (response.data && response.data.length > 0) {
        const newSuggestions = response.data.map((address: Address) => ({
          uuid: address.uuid,
          fullAddress: address.address_string,
          subAddress: `${address.city} - ${address.postcode}`,
          source: address.source,
        }));
        
        // Determine if we need frontend pagination
        const needsFrontendPagination = response.per_page >= response.total || response.last_page === 1;
        
        if (needsFrontendPagination) {
          // Store all results and use frontend pagination with Load More
          setAllSuggestions(newSuggestions);
          setUseFrontendPagination(true);
          
          // Show first batch
          const endIndex = Math.min(itemsPerPage, newSuggestions.length);
          setSuggestions(newSuggestions.slice(0, endIndex));
          setDisplayedCount(endIndex);
        } else {
          // Use API pagination
          setUseFrontendPagination(false);
          if (page === 1) {
            setSuggestions(newSuggestions);
          } else {
            setSuggestions(prev => prev ? [...prev, ...newSuggestions] : newSuggestions);
          }
        }
        
        setHasMore(response.current_page < response.last_page);
        setCurrentPage(response.current_page);
        setTotal(response.total);
      } else {
        if (page === 1) {
          setSuggestions([]);
          setAllSuggestions([]);
          setDisplayedCount(0);
        }
        setHasMore(false);
        setUseFrontendPagination(false);
      }
    } catch (error) {
      if (page === 1) {
        setSuggestions([]);
        setAllSuggestions([]);
        setDisplayedCount(0);
        setTotal(0);
      }
      setHasMore(false);
      setUseFrontendPagination(false);
    } finally {
      setIsSearching(false);
    }
  };

  const loadMoreResults = async () => {
    if (currentQuery && !isSearching) {
      if (useFrontendPagination && allSuggestions) {
        // Fake load more - show next batch from stored results
        const nextIndex = displayedCount;
        const endIndex = Math.min(nextIndex + itemsPerPage, allSuggestions.length);
        const newBatch = allSuggestions.slice(nextIndex, endIndex);
        
        setSuggestions(prev => prev ? [...prev, ...newBatch] : newBatch);
        setDisplayedCount(endIndex);
      } else if (hasMore) {
        // Real API load more
        await fetchAddressSuggestions(currentQuery, currentPage + 1);
      }
    }
  };


  // Delay search
  const debouncedFetchAddressSuggestions = useCallback(debounce(fetchAddressSuggestions, 1000), []);

    return (
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            id={name}
            name={name}
            value={address}
            disabled={isSearching}
            onChange={handleChange}
            placeholder='Get started by typing your address...'
            className="border-b-2 outline-none rounded-lg border-b-fred-purple-500 w-full p-4 md:p-6 text-lg md:text-3xl shadow-md focus:shadow-lg focus:border-b-fred-blue-500 hover:border-b-fred-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
          />
          {isSearching && (
            <div role="status" className='absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none'>
              <svg aria-hidden="true" className="inline w-12 h-12 text-gray-200 animate-spin fill-fred-blue-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
              </svg>
            </div>
          )}
          {!isSearching && address && (
            <div role="status" className='absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer'>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1" stroke="currentColor" className="inline w-12 h-12" onClick={() => {
                setAddress('');
                setSuggestions(null)
              }}>
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </div>
          )}
        </div>

        {suggestions && suggestions.length > 0 && (
          <ul className="divide-y divide-gray-200 mt-2 bg-white z-10 w-full border rounded-lg p-1 sm:p-2">
            {suggestions.map((suggestion, index) => (
              <li
                key={index}
                className="p-1 sm:p-2 sm:pb-4 cursor-pointer hover:bg-fred-blue-50"
              >
                <AddressItem address={suggestion} onSelect={(suggestion) => onSelectAddress(suggestion)} />
              </li>
            ))}
          </ul>
        )}
        {suggestions && suggestions.length === 0 && <AddressNotFound />}
        
        {/* Unified Load More Button for both API and Frontend pagination */}
        {suggestions && suggestions.length > 0 && (
          <div className="mt-2 text-center">
            {/* Show result count for both scenarios */}
            <div className="text-sm text-gray-600 mb-2">
              {useFrontendPagination && allSuggestions ? (
                <>Showing {displayedCount} of {allSuggestions.length} results</>
              ) : (
                <>Showing {suggestions.length} of {total} results</>
              )}
            </div>
            
            {/* Load More button - appears when there are more results */}
            {(hasMore || (useFrontendPagination && allSuggestions && displayedCount < allSuggestions.length)) && (
              <button
                type="button"
                onClick={loadMoreResults}
                disabled={isSearching}
                className="px-4 py-2 bg-fred-blue-500 text-white rounded-lg hover:bg-fred-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isSearching ? 'Loading...' : 'Load More'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

export default AddressSearch;
